import { PriceRange } from '@/components/blocks/PriceRange';
import { facetChipsSX } from '@/components/content/CatalogEntryList/styles/facetChips';
import {
	FacetRatingStars,
	isRatingLabel,
} from '@/components/content/FacetNavigation/parts/FacetRatingStars';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { getCurrencyFromContext } from '@/utils/getCurrencyFromContext';
import { parseHTML } from '@/utils/parseHTMLModern';
import { Button, Chip, Stack, Typography } from '@mui/material';
import { FC, useContext, useMemo } from 'react';
import {
	MATERIAL_SYMBOL_ICONS,
	OpenSansFontWeights,
} from 'shared/custom/constants/commonConstants';

export const CatalogEntryListFacetChips: FC = () => {
	const {
		onDeleteAll,
		onFacetDelete,
		onPriceSelectionDelete,
		selectedFacet,
		facetEntries,
		filteredParams,
	} = useContext(ContentContext) as ReturnType<typeof useCatalogEntryList>;
	const productGridNLS = useLocalization('ProductGrid');
	const { Labels } = useLocalization('ProductFilter');
	const { minPrice = -1, maxPrice = -1 } = filteredParams;
	const { user } = useUser();
	const { settings } = useSettings();
	const { defaultCurrency } = settings;
	const currency = useMemo(
		() => getCurrencyFromContext(user?.context) ?? defaultCurrency,
		[user, defaultCurrency]
	);
	const hasPriceFacet = minPrice > -1 && maxPrice > -1;

	if (!selectedFacet.length && !hasPriceFacet) return;

	return (
		<Stack direction="row" alignItems="center" spacing={1} sx={facetChipsSX.stackContainer}>
			<Typography
				sx={facetChipsSX.filterTitle}
				variant="body1"
				color="grey.500"
				fontWeight={OpenSansFontWeights.semibold}
			>{`${Labels.filterBy.t()} (${
				Object.keys(selectedFacet).length + Number(hasPriceFacet)
			})`}</Typography>

			<Stack direction="row" spacing={1} sx={facetChipsSX.chipsContainer}>
				{selectedFacet.map((facetValue) => {
					const entry = facetEntries.find((e) => e.value === facetValue);
					const entryLabel = entry?.label ?? '';
					const isRating = isRatingLabel(entryLabel);

					return (
						<Chip
							key={facetValue}
							label={
								isRating ? (
									<FacetRatingStars label={entryLabel} />
								) : (
									<Typography
										variant="bodyBaseline"
										color="grey.500"
										fontWeight={OpenSansFontWeights.semibold}
									>
										{entryLabel && parseHTML(entryLabel)}
									</Typography>
								)
							}
							onClick={onFacetDelete(facetValue)}
							onDelete={onFacetDelete(facetValue)}
							data-testid={`catalog-entry-list-${facetValue}-selected-facet-chip`}
							id={`catalog-entry-list-${facetValue}-selected-facet-chip`}
							className="applied-filter-chip"
							size="medium"
							sx={facetChipsSX.chips}
							deleteIcon={<DynamicIcon name={MATERIAL_SYMBOL_ICONS.cancel} />}
						/>
					);
				})}
				{hasPriceFacet && (
					<Chip
						label={
							<Typography
								variant="bodyBaseline"
								color="grey.500"
								fontWeight={OpenSansFontWeights.semibold}
							>
								<PriceRange min={minPrice} max={maxPrice} />
							</Typography>
						}
						onClick={onPriceSelectionDelete}
						onDelete={onPriceSelectionDelete}
						data-testid="catalog-entry-list-formatted-price-chip"
						id="catalog-entry-list-formatted-price-chip"
						className="applied-filter-chip"
						size="medium"
						sx={facetChipsSX.chips}
						deleteIcon={<DynamicIcon name={MATERIAL_SYMBOL_ICONS.cancel} />}
					/>
				)}
			</Stack>

			<Button
				id="clearFiltersLink"
				data-testid="clear-all-facets"
				onClick={onDeleteAll}
				sx={facetChipsSX.btnClear}
			>
				<Typography variant="bodyBaseline" fontWeight={OpenSansFontWeights.regular}>
					{productGridNLS.Actions.clearAll.t()}
				</Typography>
			</Button>
		</Stack>
	);
};
